/**
 * The setup checklist, including the content-folder request.
 *
 * Server truth throughout: `checklist` rides along on GET /api/brand.
 * It guides setup but never blocks briefing; nothing in this file decides
 * whether a client may submit work.
 *
 * The card removes itself once every required item is done. A checklist that
 * stays on screen saying "3 of 3" is furniture.
 *
 * @module web/screens/app/overview/Checklist
 */
import { Fragment, useEffect, useRef, useState } from "react";
import { patch } from "../../../api/client.ts";
import type { ContentFolderResponse } from "../../../api/types.ts";
import { useBrand } from "../../../state/BrandContext.tsx";
import { CK_COPY, needsContentFolder, requiredProgress } from "./setup.ts";

export function Checklist({ focusContent = 0, onContentAttached }: { focusContent?: number; onContentAttached?: () => void }) {
  const { brand, refresh, notify } = useBrand();
  const [contentOpen, setContentOpen] = useState(false);
  const [highlight, setHighlight] = useState(false);
  const contentButton = useRef<HTMLButtonElement>(null);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<{ tone: "" | "ok" | "err"; text: string } | null>(null);

  const block = brand?.checklist ?? null;
  const progress = requiredProgress(block);
  useEffect(() => {
    if (!focusContent) return;
    const button = contentButton.current;
    if (!button) return;
    setHighlight(true);
    button.focus({ preventScroll: true });
    button.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "center" });
    const timer = window.setTimeout(() => setHighlight(false), 2400);
    return () => window.clearTimeout(timer);
  }, [focusContent]);
  if (!block || (progress.complete && !needsContentFolder(block) && !focusContent && !contentOpen)) return null;

  async function attach(): Promise<void> {
    const value = url.trim();
    if (!value || busy) return;
    setBusy(true);
    // Feedback goes on the BUTTON, not into `.status-msg`: that class is
    // display:none unless it is .ok or .err, so the live page's "Checking…"
    // sets text on a hidden element and shows nothing. The check fetches Drive
    // and is not instant, so there has to be something.
    setStatus(null);
    try {
      const d = await patch<ContentFolderResponse>("/api/brand/content-folder", { url: value });
      setUrl("");
      // Say exactly what happened. Attached-but-unreadable is not "all good":
      // the editor has to open it, and only the brand can fix the sharing.
      //
      // As a TOAST, not into this card. The folder is usually the last
      // outstanding item, so attaching it completes the checklist and this card
      // removes itself in the same breath - taking the answer with it. The live
      // page writes the sentence into an element it is about to hide.
      notify(
        d.contentFolderStatus === "ok"
          ? "Folder attached - we can read it."
          : "Folder attached. We couldn't read it from here, so check the sharing settings if your editor needs it.",
        "success",
      );
      await refresh();
      if (d.contentFolderStatus === "ok") onContentAttached?.();
    } catch {
      setStatus({ tone: "err", text: "That does not look like a Google Drive folder link." });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="glass ck-card fade-in" id="checklist-card" aria-labelledby="ck-title">
      <div className="ck-head">
        <div className="ck-title" id="ck-title">{progress.complete ? "Your content" : "Finish your setup"}</div>
        {!progress.complete && <div className="ck-count" id="ck-count">{progress.done} of {progress.total}</div>}
      </div>
      <div className="ck-list" id="ck-list">
        {block.items.filter(item => !progress.complete || item.id === "content").map(item => {
          const copy = CK_COPY[item.id] ?? { label: item.id, sub: "" };
          const action = item.done ? copy.done : copy.open;
          const sub = item.done ? (copy.doneSub ?? copy.sub) : copy.sub;
          const accountPayment = item.id === "payment" && brand?.billingOwner === false;
          return (
            <Fragment key={item.id}>
              <div className={`ck-row${item.done ? " done" : ""}${item.id === "content" ? " ck-content-row" : ""}`}>
                <span className="ck-box" aria-hidden="true" />
                <span className="ck-label">
                  {copy.label}
                  <span className="ck-sub">{accountPayment && !item.done ? "The account Owner adds a card for all brands." : sub}</span>
                  {/* A warning is a sentence, not a chip - at 393px a pill would
                      overlap the label. */}
                  {item.warning && <span className="ck-warn">{item.warning}</span>}
                </span>
                {item.optional && <span className="ck-badge">Optional</span>}
                {item.id === "content" && <button type="button" ref={contentButton} id="ck-add-content"
                  className={`ck-act ck-add-content${highlight ? " highlighted" : ""}`} aria-expanded={contentOpen} aria-controls="ck-folder-form"
                  onClick={() => { setContentOpen(open => !open); setHighlight(false); }}>Add Content</button>}
                {/* Only completed rows link out. Board setup is automatic on
                    first confirmation, so an unlinked board has no control. */}
                {action?.kind === "tab" && !accountPayment && (
                  <a className="ck-act" href={`#${action.tab}`}>{action.cta}</a>
                )}
              </div>
              {/* Directly under the row it belongs to, not at the foot of the
                  card. It used to render after the whole list, which put the
                  Drive field under "Your board" - Alan drew an arrow from the
                  field back up to the right row, having read it as the board's. */}
              {item.id === "content" && contentOpen && (
                <div className="ck-form" id="ck-folder-form">
                  <input
                    id="ck-folder-url"
                    type="url"
                    inputMode="url"
                    placeholder="https://drive.google.com/drive/folders/…"
                    aria-label="Google Drive content folder link"
                    value={url}
                    onChange={e => setUrl(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); void attach(); } }}
                  />
                  <button className="btn btn-brand" onClick={() => void attach()} disabled={busy || !url.trim()}>
                    {busy ? "Checking…" : "Attach"}
                  </button>
                </div>
              )}
            </Fragment>
          );
        })}
      </div>
      <div className={`status-msg${status?.tone ? ` ${status.tone}` : ""}`} id="ck-status">
        {status?.text ?? ""}
      </div>
    </section>
  );
}
